// BHP-1064: invoices overview on Backend2.0 (Hydra `GET /api/invoices`),
// Tailwind. Replaces the legacy `/api/v1/{customer}/invoice` fan-out, the
// react-bootstrap tile/list switch and the Zustand hand-off to the detail page.
//
// The page used to be "my invoices of the last 3 months" — a client-side trim,
// because there was no pagination and the whole list came down at once. It is
// now the full list, paginated, newest first — ordered by the backend
// (BHP-1064), not by anything this page asks for.
//
// Amounts come from the backend: BHP-1110 computes them with the same rounding
// rule Exact Online applies, so nothing here multiplies or sums.
//
// BHP-1160: the list only ever contains finalized invoices — the backend keeps
// drafts out of the portal, so there is nothing to filter here. The amounts stay:
// a list needs something to scan by, and unlike the detail page this is not a
// second rendering of the invoice document. The date shown is when the invoice
// became final, not when our row happened to be created.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDate, formatMoney } from '../../features/invoice/invoice';
import { PaymentStatusBadge } from '../../features/invoice/ui/PaymentStatusBadge';
import { useInvoices } from '../../features/invoice/application/invoiceQueries';
import { PAGE_SIZE } from '../../shared/api/useHydra';
import { toUserMessage } from '../../shared/http/errorMessage';

export default function LastInvoices(): JSX.Element {
  const [page, setPage] = useState(1);
  const { data, isPending, isError, error, isFetching } = useInvoices(page);

  const invoices = data?.items ?? [];
  const total = data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="mx-auto max-w-4xl">
      <h2 className="mb-4 text-lg font-semibold text-slate-900">My invoices</h2>

      {isPending ? (
        <p className="text-sm text-slate-500">Loading invoices…</p>
      ) : isError ? (
        <div
          role="alert"
          className="rounded-lg border border-solid border-rose-200 bg-rose-50 p-4 text-sm text-rose-700"
        >
          {toUserMessage(error, 'Could not load invoices. Please try again.')}
        </div>
      ) : invoices.length === 0 ? (
        <div className="rounded-xl border border-solid border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
          No invoices for this customer.
        </div>
      ) : (
        <>
          <ul className="overflow-hidden rounded-xl border border-solid border-slate-200 bg-white">
            {invoices.map((invoice) => (
              <li
                key={invoice.uuid}
                className="flex flex-wrap items-center justify-between gap-3 border-0 border-b border-solid border-slate-100 px-4 py-3 last:border-b-0"
              >
                <span className="flex min-w-0 items-center gap-3">
                  <span className="font-medium text-slate-800">
                    {invoice.invoiceNumber ?? formatDate(invoice.finalizedAt)}
                  </span>
                  {invoice.invoiceNumber === null ? null : (
                    <span className="text-xs text-slate-400">
                      {formatDate(invoice.finalizedAt)}
                    </span>
                  )}
                  <PaymentStatusBadge invoice={invoice} />
                  {invoice.isRecurring === true ? (
                    <span className="text-xs text-slate-400">recurring</span>
                  ) : null}
                </span>
                <span className="flex items-center gap-4">
                  <span className="text-right">
                    <span className="block font-medium text-slate-800">
                      {formatMoney(invoice.totalInclVat, invoice.currency)}
                    </span>
                    <span className="block text-xs text-slate-400">
                      incl. VAT
                    </span>
                  </span>
                  <Link
                    to={`/finance/invoices/details/${invoice.uuid}`}
                    className="rounded-md border-0 bg-teal-600 px-3 py-1.5 text-sm font-medium text-white no-underline hover:bg-teal-700"
                  >
                    Details
                  </Link>
                </span>
              </li>
            ))}
          </ul>

          {lastPage > 1 ? (
            <nav
              aria-label="Invoice pages"
              className="mt-4 flex items-center justify-between gap-3 text-sm"
            >
              <button
                type="button"
                disabled={page <= 1 || isFetching}
                onClick={() => {
                  setPage((current) => Math.max(1, current - 1));
                }}
                className="rounded-md border border-solid border-slate-300 bg-white px-3 py-1.5 font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-slate-500">
                Page {page} of {lastPage} ({total} invoices)
              </span>
              <button
                type="button"
                disabled={page >= lastPage || isFetching}
                onClick={() => {
                  setPage((current) => Math.min(lastPage, current + 1));
                }}
                className="rounded-md border border-solid border-slate-300 bg-white px-3 py-1.5 font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Next
              </button>
            </nav>
          ) : null}
        </>
      )}
    </div>
  );
}
